import { Json } from "supabase/database.types";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";

import { useAuth } from "@/components/AuthProvider";
import { API_BASE } from "@/constants/base-url";
import {
  gamificationService,
  type GamificationStats,
} from "@/lib/gamification";

const KEYS = {
  BADGES: `${API_BASE}/gamification/badges`,
  SCORE: `${API_BASE}/gamification/score`,
  STREAK: `${API_BASE}/gamification/streak`,
};

export async function mutateGamification() {
  await Promise.all([
    mutate(KEYS.SCORE),
    mutate(KEYS.BADGES),
    mutate(KEYS.STREAK),
  ]);
}

export function useBadgeDefinitions() {
  return useSWR(`${API_BASE}/gamification/badge-definitions`, () =>
    gamificationService.getBadgeDefinitions()
  );
}

export function useCurrentScore() {
  const { user } = useAuth();
  return useSWR(
    user ? KEYS.SCORE : null,
    () => gamificationService.getCurrentScore().then(({ data }) => data),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  );
}

export function useGamesStats() {
  const { user } = useAuth();

  return useSWR(user ? `${API_BASE}/gamification/games-stats` : null, () =>
    gamificationService.getGamesStats(user!.id)
  );
}

export function useGamificationEvents(limit = 20) {
  const { user } = useAuth();

  return useSWR(
    user ? `${API_BASE}/gamification/events?limit=${limit}` : null,
    () => gamificationService.getUserGamificationEvents(user!.id, limit)
  );
}

export function useGamificationStats() {
  const { user } = useAuth();

  return useSWR<GamificationStats>(
    user ? `${API_BASE}/gamification/stats` : null,
    () => gamificationService.getUserStats(user!.id)
  );
}

export function useMarkNotificationAsRead() {
  return useSWRMutation<
    { success: boolean },
    Error,
    string,
    { notificationId: string }
  >(
    `${API_BASE}/gamification/notifications`,
    async (_url: string, { arg }: { arg: { notificationId: string } }) => {
      await gamificationService.markNotificationAsRead(arg.notificationId);
      return { success: true };
    }
  );
}

export function useNotifications(limit = 10) {
  const { user } = useAuth();

  return useSWR(
    user ? `${API_BASE}/gamification/notifications?limit=${limit}` : null,
    () => gamificationService.getUserNotifications(user!.id, limit)
  );
}

export function useRecordGameAttempt() {
  const { user } = useAuth();

  return useSWRMutation(
    `${API_BASE}/gamification/game-attempts`,
    async (
      _url: string,
      {
        arg,
      }: {
        arg: {
          difficulty?: number;
          gameSlug:
            | "crossmath"
            | "flashcards"
            | "number-cards"
            | "speed-challenge";
          metadata?: Json | null;
          score?: number;
          timeSeconds?: number;
        };
      }
    ) =>
      user &&
      gamificationService.recordGameAttempt(
        arg.gameSlug,
        arg.score ?? 0,
        arg.timeSeconds ?? 0,
        arg.difficulty ?? 1,
        arg.metadata
      )
  );
}

export function useUserBadges() {
  const { user } = useAuth();

  return useSWR(
    user ? KEYS.BADGES : null,
    () => gamificationService.getUserBadges(user!.id),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  );
}

export function useUserStreak() {
  const { user } = useAuth();

  return useSWR(
    user ? KEYS.STREAK : null,
    () => gamificationService.getUserStreak(user!.id),
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
      revalidateOnReconnect: true,
    }
  );
}
